import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Check,
  X,
  Clock,
  Upload,
  FileText,
  AlertTriangle,
  Paperclip,
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Users,
  Star,
  Gift,
  Loader2,
  LayoutGrid,
  Info,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { formatDateShort, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { applyDemiJournees, calculateCalendarDays, getSolidariteInfo, downloadBlob, filenameFromDisposition } from '../lib/conges';
import TeamAbsencesGrid from '../components/TeamAbsencesGrid';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Absences = () => {
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [overlapWarning, setOverlapWarning] = useState(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [validateOverlapWarning, setValidateOverlapWarning] = useState(null);
  const [pendingValidation, setPendingValidation] = useState(null);
  const [modeDecompte, setModeDecompte] = useState('ouvres');
  const [demiJourneesActives, setDemiJourneesActives] = useState(false);
  const [structureConfig, setStructureConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [solidariteInfo, setSolidariteInfo] = useState(null);
  // CP anticipé
  const [isCpAnticipeDialogOpen, setIsCpAnticipeDialogOpen] = useState(false);
  const [cpAnticipeData, setCpAnticipeData] = useState({ employee_id: '', nb_jours: '', motif: '' });
  const [employeeBalances, setEmployeeBalances] = useState({});
  // Source des soldes (logiciel de paie, bulletin, théorique) et synchronisation
  const [soldesInfo, setSoldesInfo] = useState(null);
  const [syncingSoldes, setSyncingSoldes] = useState(false);

  const [formData, setFormData] = useState({
    employee_id: '',
    type_conge: '',
    date_debut: '',
    date_fin: '',
    nb_jours: '',
    motif: '',
    demi_jour_debut: false,
    demi_jour_fin: false
  });

  const [uploadFile, setUploadFile] = useState(null);
  const [congeTypes, setCongeTypes] = useState([
    { value: 'cp', label: 'Congés payés' },
    { value: 'rtt', label: 'RTT' },
    { value: 'anciennete', label: 'Congé ancienneté' },
    { value: 'maladie', label: 'Arrêt maladie', requiresJustif: true },
    { value: 'exceptionnel', label: 'Congé exceptionnel' },
    { value: 'sans_solde', label: 'Sans solde' },
    { value: 'recuperation', label: 'Récupération' },
    { value: 'formation', label: 'Formation' }
  ]);

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calEvents, setCalEvents] = useState([]);
  const [calDepartments, setCalDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [calLoading, setCalLoading] = useState(true);
  const [typeColors, setTypeColors] = useState({});
  const [joursFeries, setJoursFeries] = useState([]);
  const [calViewMode, setCalViewMode] = useState('month');
  const [selectedCalDate, setSelectedCalDate] = useState(null);
  const [isCalDateDialogOpen, setIsCalDateDialogOpen] = useState(false);
  // 'calendrier' = grille mensuelle, 'planning' = planning mensuel de tous les collaborateurs
  const [absView, setAbsView] = useState('calendrier');
  const [calData, setCalData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const calYear = currentDate.getFullYear();
  const calMonth = currentDate.getMonth() + 1;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [absencesRes, employeesRes, configRes, typesRes, soldesInfoRes] = await Promise.all([
        axios.get(`${API_URL}/conges`),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/structure/config`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/admin/absence-types`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/conges/soldes-info`).catch(() => ({ data: null }))
      ]);
      setAbsences(absencesRes.data);
      setEmployees(employeesRes.data);
      setSoldesInfo(soldesInfoRes.data);
      // Soldes consolidés (paie > bulletin > théorique) renvoyés avec chaque salarié
      const balances = {};
      for (const emp of employeesRes.data) {
        balances[emp.id] = {
          conges_solde: emp.conges_solde || 0,
          cp_solde_n: emp.cp_solde_n,
          cp_solde_n1: emp.cp_solde_n1,
          cp_anticipe: emp.cp_anticipe || 0,
          cp_source: emp.cp_source,
          cp_a_jour_le: emp.cp_a_jour_le,
          cp_disponible: emp.cp_disponible,
          rtt_solde: emp.rtt_solde,
          soldes: emp.soldes || null
        };
      }
      setEmployeeBalances(balances);
      if (configRes.data?.conges_mode_decompte) {
        setModeDecompte(configRes.data.conges_mode_decompte);
      }
      setDemiJourneesActives(Boolean(configRes.data?.conges_demi_journees));
      setStructureConfig(configRes.data || null);
      // Charger les types d'absence custom depuis l'admin
      if (typesRes.data && typesRes.data.length > 0) {
        const customTypes = typesRes.data
          .filter(t => t.actif)
          .map(t => ({
            value: t.code,
            label: t.label,
            requiresJustif: t.justificatif_requis,
            validation_requise: t.validation_requise !== false,
            roles_saisie: t.roles_saisie || [],
            color: t.color
          }));
        if (customTypes.length > 0) {
          setCongeTypes(customTypes);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(getErrorMessage(error, 'Erreur lors du chargement des données'));
    } finally {
      setLoading(false);
    }
  };

  // Calendar data fetching
  const fetchCalendarData = useCallback(async () => {
    try {
      setCalLoading(true);
      const params = { year: calYear, month: calMonth };
      if (selectedDepartment && selectedDepartment !== 'all') {
        params.department = selectedDepartment;
      }
      const [absencesRes, joursFeriesRes] = await Promise.all([
        axios.get(`${API_URL}/calendar/absences`, { params }),
        axios.get(`${API_URL}/calendar/jours-feries`, { params: { year: calYear } }).catch(() => ({ data: [] }))
      ]);
      setCalEvents(absencesRes.data.events || []);
      setCalDepartments(absencesRes.data.departments || []);
      setTypeColors(absencesRes.data.type_colors || {});
      setCalData(absencesRes.data);
      setJoursFeries(joursFeriesRes.data || []);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    } finally {
      setCalLoading(false);
    }
  }, [calYear, calMonth, selectedDepartment]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const calculateDays = (start, end) => calculateCalendarDays(start, end);

  const calculateDaysFromApi = async (start, end, demiDebut = false, demiFin = false) => {
    if (!start || !end) return;
    try {
      const res = await axios.get(`${API_URL}/conges/calculate-days`, {
        params: { date_debut: start, date_fin: end }
      });
      setFormData(prev => ({ ...prev, nb_jours: applyDemiJournees(res.data.nb_jours, demiDebut, demiFin) }));
      setSolidariteInfo(getSolidariteInfo(res.data, structureConfig, start, end));
    } catch {
      // Fallback au calcul calendaire
      setFormData(prev => ({ ...prev, nb_jours: applyDemiJournees(calculateDays(start, end), demiDebut, demiFin) }));
      setSolidariteInfo(getSolidariteInfo(null, structureConfig, start, end));
    }
  };

  const handleDateChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    if (newData.date_debut && newData.date_fin) {
      calculateDaysFromApi(newData.date_debut, newData.date_fin, newData.demi_jour_debut, newData.demi_jour_fin);
    }
  };

  const handleDemiJourChange = (field, checked) => {
    const newData = { ...formData, [field]: checked };
    setFormData(newData);
    if (newData.date_debut && newData.date_fin) {
      calculateDaysFromApi(newData.date_debut, newData.date_fin, newData.demi_jour_debut, newData.demi_jour_fin);
    }
  };

  const handleSubmit = async (e, forceCreate = false) => {
    e?.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const url = forceCreate ? `${API_URL}/conges?force=true` : `${API_URL}/conges`;
      await axios.post(url, {
        ...formData,
        nb_jours: parseFloat(formData.nb_jours) || calculateDays(formData.date_debut, formData.date_fin)
      });
      toast.success('Demande de congé créée');
      setIsDialogOpen(false);
      setIsConfirmDialogOpen(false);
      setOverlapWarning(null);
      resetForm();
      // La liste est renvoyée triée par date décroissante : la nouvelle demande apparaît en tête
      await Promise.all([fetchData(), fetchCalendarData()]);
    } catch (error) {
      console.error('Error creating absence:', error);
      if (!forceCreate && error.response?.status === 409 && error.response?.data?.detail?.requires_confirmation) {
        // Fermer le formulaire AVANT d'ouvrir la confirmation (évite deux modales empilées)
        setIsDialogOpen(false);
        setOverlapWarning(error.response.data.detail);
        setIsConfirmDialogOpen(true);
      } else {
        // Échec (y compris après confirmation) : tout fermer et afficher un message lisible
        setIsConfirmDialogOpen(false);
        setOverlapWarning(null);
        if (forceCreate) {
          setIsDialogOpen(false);
          resetForm();
        }
        toast.error(getErrorMessage(error, 'Erreur lors de la création de la demande'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmOverlap = () => {
    handleSubmit(null, true);
  };

  // Annulation de la confirmation : on revient au formulaire (données conservées)
  const handleCancelOverlap = () => {
    setIsConfirmDialogOpen(false);
    setOverlapWarning(null);
    setIsDialogOpen(true);
  };

  const handleExportPlanning = async () => {
    setExporting(true);
    try {
      const params = { year: calYear, month: calMonth };
      if (selectedDepartment && selectedDepartment !== 'all') params.department = selectedDepartment;
      const res = await axios.get(`${API_URL}/calendar/planning-export`, { params, responseType: 'blob' });
      const filename = filenameFromDisposition(res.headers?.['content-disposition'], `planning-absences-${calYear}-${String(calMonth).padStart(2, '0')}.xlsx`);
      downloadBlob(res.data, filename);
      toast.success('Planning exporté');
    } catch (error) {
      console.error('Error exporting planning:', error);
      toast.error(getErrorMessage(error, "Erreur lors de l'export du planning"));
    } finally {
      setExporting(false);
    }
  };

  const handleValidate = async (id, action, niveau, forceValidate = false) => {
    try {
      const forceParam = forceValidate ? '&force=true' : '';
      await axios.put(`${API_URL}/conges/${id}/validate?action=${action}&niveau=${niveau}${forceParam}`);
      toast.success(action === 'approve' ? 'Congé validé' : 'Congé refusé');
      setValidateOverlapWarning(null);
      setPendingValidation(null);
      await Promise.all([fetchData(), fetchCalendarData()]);
    } catch (error) {
      console.error('Error validating:', error);
      if (!forceValidate && error.response?.status === 409 && error.response?.data?.detail?.requires_confirmation) {
        setValidateOverlapWarning(error.response.data.detail);
        setPendingValidation({ id, action, niveau });
      } else {
        setValidateOverlapWarning(null);
        setPendingValidation(null);
        toast.error(getErrorMessage(error, 'Erreur lors de la validation'));
      }
    }
  };

  const handleUploadJustificatif = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedAbsence) return;

    const formDataObj = new FormData();
    formDataObj.append('file', uploadFile);

    try {
      await axios.post(`${API_URL}/conges/${selectedAbsence.id}/justificatif`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Justificatif ajouté');
      setIsUploadDialogOpen(false);
      setUploadFile(null);
      setSelectedAbsence(null);
      fetchData();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(getErrorMessage(error, "Erreur lors de l'upload"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await axios.delete(`${API_URL}/conges/${id}`);
      toast.success('Demande supprimée');
      fetchData();
      fetchCalendarData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const handleCpAnticipe = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/conges/cp-anticipe`, {
        employee_id: cpAnticipeData.employee_id,
        nb_jours: parseFloat(cpAnticipeData.nb_jours),
        motif: cpAnticipeData.motif
      });
      toast.success('CP anticipés attribués');
      setIsCpAnticipeDialogOpen(false);
      setCpAnticipeData({ employee_id: '', nb_jours: '', motif: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de l'attribution"));
    }
  };

  const handleSyncSoldes = async () => {
    setSyncingSoldes(true);
    try {
      const res = await axios.post(`${API_URL}/conges/sync-soldes`);
      if (res.data?.success) {
        toast.success(res.data.message || `${res.data.synced_count} salarié(s) synchronisé(s)`);
      } else {
        toast.warning(res.data?.message || 'Aucun solde synchronisé');
      }
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Synchronisation des soldes impossible'));
    } finally {
      setSyncingSoldes(false);
    }
  };

  const SOLDE_SOURCE_META = {
    paie: { label: 'Paie', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    bulletin: { label: 'Bulletin', className: 'bg-blue-50 text-blue-700 border-blue-200' },
    theorique: { label: 'Théorique', className: 'bg-slate-50 text-slate-600 border-slate-200' }
  };

  const formatJourMoisAnnee = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '');

  const getEmployeeBalance = (employeeId) => {
    return employeeBalances[employeeId];
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      type_conge: '',
      date_debut: '',
      date_fin: '',
      nb_jours: '',
      motif: '',
      demi_jour_debut: false,
      demi_jour_fin: false
    });
    setSolidariteInfo(null);
  };

  // Employes tries par nom puis prenom pour les selecteurs
  const sortedEmployees = [...employees].sort((a, b) =>
    `${a.nom || ''} ${a.prenom || ''}`.localeCompare(`${b.nom || ''} ${b.prenom || ''}`, 'fr', { sensitivity: 'base' })
  );

  const filteredAbsences = absences.filter(abs => {
    const matchesSearch = abs.employee_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || abs.statut === filterStatus;
    const matchesType = filterType === 'all' || abs.type_conge === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getCongeLabel = (value) => {
    return congeTypes.find(t => t.value === value)?.label || value;
  };

  const getValidationStatusBadge = (statut) => {
    switch (statut) {
      case 'demande':
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
      case 'valide_manager':
        return <Badge className="bg-blue-100 text-blue-800"><Check className="w-3 h-3 mr-1" />Valide Manager</Badge>;
      case 'valide_rh':
        return <Badge className="bg-emerald-100 text-emerald-800"><Check className="w-3 h-3 mr-1" />Valide RH</Badge>;
      case 'refuse':
        return <Badge className="bg-red-100 text-red-800"><X className="w-3 h-3 mr-1" />Refuse</Badge>;
      default:
        return <Badge>{statut}</Badge>;
    }
  };

  const stats = {
    enAttente: absences.filter(a => a.statut === 'demande').length,
    valideManager: absences.filter(a => a.statut === 'valide_manager').length,
    valideRH: absences.filter(a => a.statut === 'valide_rh').length,
    maladies: absences.filter(a => a.type_conge === 'maladie').length
  };

  // Calendar helpers
  const getDaysInMonth = (y, m) => new Date(y, m, 0).getDate();
  const getFirstDayOfMonth = (y, m) => { const d = new Date(y, m - 1, 1).getDay(); return d === 0 ? 6 : d - 1; };
  const navigateCal = (direction) => {
    if (calViewMode === 'month') {
      setCurrentDate(new Date(calYear, calMonth - 1 + direction, 1));
    } else {
      const days = parseInt(calViewMode);
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + (direction * days));
      setCurrentDate(newDate);
    }
  };
  const goToToday = () => setCurrentDate(new Date());

  const getJourFerieByStr = (dateStr) => {
    return joursFeries.find(jf => jf.date === dateStr);
  };

  const getEventsForDateStr = (dateStr) => {
    return calEvents.filter(event => dateStr >= event.start && dateStr <= event.end);
  };

  const getCalDaysToDisplay = () => {
    if (calViewMode === 'month') {
      const daysInMonth = getDaysInMonth(calYear, calMonth);
      const firstDay = getFirstDayOfMonth(calYear, calMonth);
      const days = [];
      for (let i = 0; i < firstDay; i++) {
        days.push(null);
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${calYear}-${String(calMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        days.push({ day: d, dateStr, date: new Date(calYear, calMonth - 1, d) });
      }
      return days;
    } else {
      const numDays = parseInt(calViewMode);
      const days = [];
      const startDate = new Date(currentDate);
      if (numDays === 7) {
        const dayOfWeek = startDate.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startDate.setDate(startDate.getDate() + diff);
      }
      for (let i = 0; i < numDays; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        days.push({ day: d.getDate(), dateStr: `${y}-${m}-${dd}`, date: d });
      }
      return days;
    }
  };

  const handleCalDateClick = (dateStr) => {
    const dayEvents = getEventsForDateStr(dateStr);
    setSelectedCalDate({ dateStr, events: dayEvents });
    setIsCalDateDialogOpen(true);
  };

  const formatDateFr = (dateStr) => {
    try {
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return dateStr; }
  };

  const getCalViewTitle = () => {
    if (calViewMode === 'month') {
      return `${monthNames[calMonth - 1]} ${calYear}`;
    }
    const days = getCalDaysToDisplay().filter(Boolean);
    if (days.length === 0) return '';
    const first = days[0];
    const last = days[days.length - 1];
    return `${first.date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${last.date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  const calDaysToDisplay = getCalDaysToDisplay();
  const monthNames = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const todayStr = (() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`; })();

  const calViewOptions = [
    { value: '7', label: '1 semaine' },
    { value: '14', label: '14 jours' },
    { value: 'month', label: '1 mois' }
  ];

  const statusLabels = {
    demande: 'En attente',
    valide_manager: 'Valide manager',
    valide_rh: 'Valide RH',
    refuse: 'Refuse'
  };

  const typeLabels = {
    cp: 'Conges Payes', rtt: 'RTT', maladie: 'Maladie', sans_solde: 'Sans Solde',
    recuperation: 'Recuperation', exceptionnel: 'Exceptionnel', formation: 'Formation'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="absences-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Conges & Absences</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Gestion des conges, absences et calendrier de l'equipe
          </p>
          {soldesInfo && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1" data-testid="soldes-source-info">
              <Info className="w-3 h-3" />
              {soldesInfo.payroll_connected
                ? (soldesInfo.sync_disponible
                  ? `Soldes de congés : ${soldesInfo.logiciel_paie}${soldesInfo.derniere_sync?.created_at ? ` (dernière synchronisation le ${formatJourMoisAnnee(soldesInfo.derniere_sync.created_at)})` : ' (jamais synchronisé)'}`
                  : `Soldes de congés : lus sur les bulletins de ${soldesInfo.logiciel_paie}, sinon valeur théorique`)
                : `Soldes de congés théoriques (aucun logiciel de paie connecté, période du ${formatJourMoisAnnee(soldesInfo.periode_reference?.debut_n)} au ${formatJourMoisAnnee(soldesInfo.periode_reference?.fin_n)})`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
        {soldesInfo?.sync_disponible && soldesInfo?.peut_synchroniser && (
          <Button variant="outline" onClick={handleSyncSoldes} disabled={syncingSoldes} data-testid="sync-soldes-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncingSoldes ? 'animate-spin' : ''}`} />
            {syncingSoldes ? 'Actualisation...' : 'Actualiser depuis la paie'}
          </Button>
        )}
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F5156] hover:bg-[#0d464a]" data-testid="add-absence-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle demande
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouvelle demande de conge</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employe *</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData({ ...formData, employee_id: value })}
                >
                  <SelectTrigger data-testid="absence-employee-select">
                    <SelectValue placeholder="Selectionner un employe" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.nom?.toUpperCase()} {emp.prenom}
                        <span className="text-slate-400 ml-2">({Number(emp.cp_disponible ?? emp.conges_solde ?? 0).toFixed(1)}j dispo)</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type de conge *</Label>
                <Select
                  value={formData.type_conge}
                  onValueChange={(value) => setFormData({ ...formData, type_conge: value })}
                >
                  <SelectTrigger data-testid="absence-type-select">
                    <SelectValue placeholder="Selectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {congeTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                        {type.requiresJustif && <span className="text-red-500 ml-1">*</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.type_conge === 'maladie' && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Un justificatif sera requis pour valider l'arret maladie
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de debut *</Label>
                  <Input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => handleDateChange('date_debut', e.target.value)}
                    required
                    data-testid="absence-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin *</Label>
                  <Input
                    type="date"
                    value={formData.date_fin}
                    onChange={(e) => handleDateChange('date_fin', e.target.value)}
                    required
                    data-testid="absence-end-date-input"
                  />
                </div>
              </div>
              {demiJourneesActives && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="demi_jour_debut"
                      checked={formData.demi_jour_debut}
                      onChange={(e) => handleDemiJourChange('demi_jour_debut', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="demi_jour_debut" className="text-sm cursor-pointer">
                      Début l'après-midi (½ jour)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="demi_jour_fin"
                      checked={formData.demi_jour_fin}
                      onChange={(e) => handleDemiJourChange('demi_jour_fin', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="demi_jour_fin" className="text-sm cursor-pointer">
                      Fin le midi (½ jour)
                    </Label>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Nombre de jours ({modeDecompte === 'ouvrables' ? 'ouvrables' : 'ouvres'})</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.nb_jours}
                  onChange={(e) => setFormData({ ...formData, nb_jours: e.target.value })}
                  placeholder="Calcule automatiquement"
                  data-testid="absence-days-input"
                />
                <p className="text-xs text-slate-500">
                  {modeDecompte === 'ouvrables' ? 'Decompte en jours ouvrables (lundi-samedi, hors jours feries)' : 'Decompte en jours ouvres (lundi-vendredi, hors jours feries)'}
                </p>
                {solidariteInfo && (
                  <p className="text-xs text-purple-700 flex items-center gap-1" data-testid="absence-solidarite-info">
                    <Info className="w-3 h-3" />
                    {solidariteInfo}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Motif</Label>
                <Textarea
                  value={formData.motif}
                  onChange={(e) => setFormData({ ...formData, motif: e.target.value })}
                  placeholder="Precisez le motif si necessaire..."
                  rows={2}
                  data-testid="absence-motif-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={submitting}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]" disabled={submitting} data-testid="absence-submit-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement…
                    </>
                  ) : (
                    'Soumettre la demande'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">En attente</p>
                <p className="text-2xl font-bold text-amber-500">{stats.enAttente}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Valide Manager</p>
                <p className="text-2xl font-bold text-blue-500">{stats.valideManager}</p>
              </div>
              <Check className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Valide RH</p>
                <p className="text-2xl font-bold text-emerald-500">{stats.valideRH}</p>
              </div>
              <Check className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Arrets maladie</p>
                <p className="text-2xl font-bold text-red-500">{stats.maladies}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par employe..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="absence-search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                {congeTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40" data-testid="absence-filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="demande">En attente</SelectItem>
                <SelectItem value="valide_manager">Valide Manager</SelectItem>
                <SelectItem value="valide_rh">Valide RH</SelectItem>
                <SelectItem value="refuse">Refuse</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Absences Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="table-header">Employe</TableHead>
                  <TableHead className="table-header hidden sm:table-cell">Solde CP</TableHead>
                  <TableHead className="table-header">Type</TableHead>
                  <TableHead className="table-header hidden md:table-cell">Periode</TableHead>
                  <TableHead className="table-header text-center">Jours</TableHead>
                  <TableHead className="table-header">Statut</TableHead>
                  <TableHead className="table-header text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAbsences.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Aucune demande trouvee
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAbsences.map((absence) => (
                    <TableRow key={absence.id} className="table-row" data-testid={`absence-row-${absence.id}`}>
                      <TableCell className="table-cell">
                        <p className="font-medium">{absence.employee_name}</p>
                      </TableCell>
                      <TableCell className="table-cell hidden sm:table-cell">
                        {(() => {
                          const bal = getEmployeeBalance(absence.employee_id);
                          if (!bal) return <span className="text-slate-400">-</span>;
                          const solde = bal.conges_solde + (bal.cp_anticipe || 0);
                          const meta = SOLDE_SOURCE_META[bal.cp_source] || SOLDE_SOURCE_META.theorique;
                          const titre = [
                            bal.soldes?.methode,
                            bal.cp_a_jour_le ? `À jour au ${formatJourMoisAnnee(bal.cp_a_jour_le)}` : null,
                            bal.cp_disponible !== undefined && bal.cp_disponible !== null ? `Disponible après demandes en cours : ${bal.cp_disponible.toFixed(1)} j` : null,
                            ...(bal.soldes?.avertissements || [])
                          ].filter(Boolean).join('\n');
                          return (
                            <div className="text-xs" title={titre}>
                              <span className={`font-semibold ${solde > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {solde.toFixed(1)}j
                              </span>
                              <span className={`ml-1 inline-block rounded border px-1 py-0 text-[10px] leading-4 ${meta.className}`}>{meta.label}</span>
                              {bal.cp_solde_n !== undefined && bal.cp_solde_n !== null && (
                                <div className="text-slate-400">
                                  N: {Number(bal.cp_solde_n).toFixed(1)} | N-1: {Number(bal.cp_solde_n1 || 0).toFixed(1)}
                                  {bal.cp_a_jour_le && <span className="ml-1">au {formatJourMoisAnnee(bal.cp_a_jour_le).slice(0, 5)}</span>}
                                </div>
                              )}
                              {bal.cp_anticipe > 0 && (
                                <div className="text-blue-500">+{bal.cp_anticipe}j anticip.</div>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="table-cell">
                        <div className="flex items-center gap-2">
                          <span>{getCongeLabel(absence.type_conge)}</span>
                          {absence.type_conge === 'maladie' && (
                            absence.justificatif_url ? (
                              <Paperclip className="w-4 h-4 text-emerald-500" title="Justificatif joint" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-amber-500" title="Justificatif manquant" />
                            )
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="table-cell hidden md:table-cell">
                        <div className="text-sm">
                          <p>{formatDateShort(absence.date_debut)}</p>
                          <p className="text-slate-500">au {formatDateShort(absence.date_fin)}</p>
                        </div>
                      </TableCell>
                      <TableCell className="table-cell text-center">
                        <span className="font-semibold">{absence.nb_jours}</span>
                      </TableCell>
                      <TableCell className="table-cell">
                        {getValidationStatusBadge(absence.statut)}
                      </TableCell>
                      <TableCell className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {absence.statut === 'demande' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-blue-600 hover:bg-blue-50"
                                onClick={() => handleValidate(absence.id, 'approve', 'manager')}
                                title="Valider (Manager)"
                                data-testid={`validate-manager-${absence.id}`}
                              >
                                <Check className="w-4 h-4" />
                                <span className="text-xs ml-1">M</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleValidate(absence.id, 'reject', 'manager')}
                                title="Refuser"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {absence.statut === 'valide_manager' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-emerald-600 hover:bg-emerald-50"
                                onClick={() => handleValidate(absence.id, 'approve', 'rh')}
                                title="Valider (RH)"
                                data-testid={`validate-rh-${absence.id}`}
                              >
                                <Check className="w-4 h-4" />
                                <span className="text-xs ml-1">RH</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleValidate(absence.id, 'reject', 'rh')}
                                title="Refuser"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {absence.type_conge === 'maladie' && !absence.justificatif_url && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-600 hover:bg-amber-50"
                              onClick={() => { setSelectedAbsence(absence); setIsUploadDialogOpen(true); }}
                              title="Ajouter justificatif"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {absence.type_conge === 'maladie' && (
                                <DropdownMenuItem onClick={() => { setSelectedAbsence(absence); setIsUploadDialogOpen(true); }}>
                                  <Upload className="w-4 h-4 mr-2" />
                                  {absence.justificatif_url ? 'Remplacer justificatif' : 'Ajouter justificatif'}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => {
                                setCpAnticipeData({ employee_id: absence.employee_id, nb_jours: '', motif: '' });
                                setIsCpAnticipeDialogOpen(true);
                              }}>
                                <Gift className="w-4 h-4 mr-2" />
                                Attribuer CP anticipe
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(absence.id)} className="text-red-600">
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog CP Anticipe */}
      <Dialog open={isCpAnticipeDialogOpen} onOpenChange={setIsCpAnticipeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attribuer des CP anticipes</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCpAnticipe} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Employe</Label>
              <Select
                value={cpAnticipeData.employee_id}
                onValueChange={(value) => setCpAnticipeData({ ...cpAnticipeData, employee_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectionner un employe" />
                </SelectTrigger>
                <SelectContent>
                  {sortedEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nom?.toUpperCase()} {emp.prenom}
                      <span className="text-slate-400 ml-2">({Number(emp.cp_disponible ?? emp.conges_solde ?? 0).toFixed(1)}j dispo)</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nombre de jours *</Label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                value={cpAnticipeData.nb_jours}
                onChange={(e) => setCpAnticipeData({ ...cpAnticipeData, nb_jours: e.target.value })}
                placeholder="Ex: 2.5"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Motif</Label>
              <Textarea
                value={cpAnticipeData.motif}
                onChange={(e) => setCpAnticipeData({ ...cpAnticipeData, motif: e.target.value })}
                placeholder="Raison de l'attribution anticipee..."
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsCpAnticipeDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]">
                Attribuer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============================================================ */}
      {/* CALENDRIER DES ABSENCES */}
      {/* ============================================================ */}
      <div className="pt-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-[#0F5156]" />
            {absView === 'planning' ? 'Planning mensuel des absences' : 'Calendrier des absences'}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
              <Button
                variant={absView === 'calendrier' ? 'default' : 'ghost'}
                size="sm"
                className={`rounded-none ${absView === 'calendrier' ? 'bg-[#0F5156] hover:bg-[#0d464a]' : ''}`}
                onClick={() => setAbsView('calendrier')}
                data-testid="absences-view-calendrier"
              >
                <CalendarIcon className="w-4 h-4 mr-1" />
                Calendrier
              </Button>
              <Button
                variant={absView === 'planning' ? 'default' : 'ghost'}
                size="sm"
                className={`rounded-none ${absView === 'planning' ? 'bg-[#0F5156] hover:bg-[#0d464a]' : ''}`}
                onClick={() => setAbsView('planning')}
                data-testid="absences-view-planning"
              >
                <LayoutGrid className="w-4 h-4 mr-1" />
                Planning mensuel
              </Button>
            </div>
            {absView === 'calendrier' && (
            <Select value={calViewMode} onValueChange={setCalViewMode}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {calViewOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-[220px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Tous les departements" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les departements</SelectItem>
                {calDepartments.map(dept => (
                  <SelectItem key={dept.code || dept} value={dept.code || dept}>
                    <div className="flex items-center gap-2">
                      {dept.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />}
                      {dept.label || dept}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {absView === 'planning' ? (
          <TeamAbsencesGrid
            year={calYear}
            month={calMonth}
            data={calData}
            joursFeries={joursFeries}
            loading={calLoading}
            onNavigate={(direction) => setCurrentDate(new Date(calYear, calMonth - 1 + direction, 1))}
            onToday={goToToday}
            onExport={handleExportPlanning}
            exporting={exporting}
            showToolbar
            showLegend
          />
        ) : (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigateCal(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => navigateCal(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Aujourd'hui
                </Button>
              </div>
              <CardTitle className="text-xl">
                {getCalViewTitle()}
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Users className="w-4 h-4" />
                {calEvents.length} absence(s)
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {calLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F5156]"></div>
              </div>
            ) : (
              <>
                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {calViewMode === 'month' ? (
                    dayNames.map(day => (
                      <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                        {day}
                      </div>
                    ))
                  ) : (
                    calDaysToDisplay.filter(Boolean).map((d, idx) => (
                      <div key={idx} className="text-center text-sm font-medium text-slate-500 py-2">
                        {d.date.toLocaleDateString('fr-FR', { weekday: 'short' })}
                      </div>
                    ))
                  )}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1"
                     style={calViewMode === '14' ? { gridTemplateColumns: 'repeat(7, 1fr)' } : undefined}>
                  {calDaysToDisplay.map((dayInfo, i) => {
                    if (!dayInfo) {
                      return <div key={`empty-${i}`} className="min-h-[90px] bg-slate-50 dark:bg-slate-800/50 rounded-lg"></div>;
                    }

                    const { day, dateStr, date } = dayInfo;
                    const dayEvents = getEventsForDateStr(dateStr);
                    const jourFerie = getJourFerieByStr(dateStr);
                    const isToday = dateStr === todayStr;
                    const dayOfWeek = date.getDay();
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                    return (
                      <div
                        key={dateStr}
                        onClick={() => handleCalDateClick(dateStr)}
                        className={`min-h-[90px] p-1 rounded-lg border transition-colors cursor-pointer ${
                          jourFerie
                            ? 'border-red-300 bg-red-50 dark:bg-red-900/20'
                            : isToday
                              ? 'border-[#0F5156] bg-[#0F5156]/5'
                              : isWeekend
                                ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50'
                                : 'border-slate-200 dark:border-slate-700 hover:border-[#0F5156]/50 hover:bg-slate-50 dark:hover:bg-slate-800/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className={`text-sm font-medium ${
                            jourFerie ? 'text-red-600' : isToday ? 'text-[#0F5156]' : 'text-slate-600 dark:text-slate-400'
                          }`}>
                            {calViewMode !== 'month' ? (
                              <span>{day}/{String(date.getMonth() + 1).padStart(2, '0')}</span>
                            ) : (
                              day
                            )}
                          </div>
                          {jourFerie && <Star className="w-3 h-3 text-red-500 fill-red-500" />}
                        </div>
                        {jourFerie && (
                          <div className="text-xs text-red-600 font-medium truncate mb-1" title={jourFerie.nom}>
                            {jourFerie.nom}
                          </div>
                        )}
                        <div className="space-y-0.5 overflow-y-auto max-h-[45px]">
                          {dayEvents.slice(0, 2).map((event, idx) => (
                            <div
                              key={`${event.id}-${idx}`}
                              className="text-xs px-1 py-0.5 rounded truncate hover:opacity-80"
                              style={{
                                backgroundColor: event.color + '20',
                                color: event.color,
                                borderLeft: `3px solid ${event.color}`
                              }}
                              title={`${event.employee_name} - ${typeLabels[event.type] || event.type}`}
                            >
                              {event.employee_name?.split(' ')[0]}
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <div className="text-xs text-slate-500 text-center">
                              +{dayEvents.length - 2}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {/* Legend */}
        {absView === 'calendrier' && (
        <div className="flex flex-wrap gap-3 mt-4 px-1">
          <div className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 rounded bg-red-500 flex items-center justify-center">
              <Star className="w-2.5 h-2.5 text-white fill-white" />
            </div>
            <span className="text-xs text-slate-500">Jour ferie</span>
          </div>
          {Object.entries(typeColors).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1.5">
              <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: color }} />
              <span className="text-xs text-slate-500">{typeLabels[type] || type}</span>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Date Click Dialog - Show absent employees */}
      <Dialog open={isCalDateDialogOpen} onOpenChange={setIsCalDateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-[#0F5156]" />
              Absences du {selectedCalDate ? formatDateFr(selectedCalDate.dateStr) : ''}
            </DialogTitle>
          </DialogHeader>
          {selectedCalDate && (
            <div className="mt-4">
              {selectedCalDate.events.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  Aucun collaborateur absent ce jour
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-500 mb-4">
                    {selectedCalDate.events.length} collaborateur(s) absent(s)
                  </p>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Collaborateur</TableHead>
                          <TableHead>Departement</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Du</TableHead>
                          <TableHead>Au</TableHead>
                          <TableHead>Duree</TableHead>
                          <TableHead>Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCalDate.events.map(event => (
                          <TableRow key={event.id}>
                            <TableCell className="font-medium">{event.employee_name}</TableCell>
                            <TableCell>{event.department || '-'}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                style={{ borderColor: event.color, color: event.color }}
                              >
                                {typeLabels[event.type] || event.type}
                              </Badge>
                            </TableCell>
                            <TableCell>{new Date(event.start + 'T00:00:00').toLocaleDateString('fr-FR')}</TableCell>
                            <TableCell>{new Date(event.end + 'T00:00:00').toLocaleDateString('fr-FR')}</TableCell>
                            <TableCell>{event.nb_jours} j</TableCell>
                            <TableCell>
                              <Badge variant={event.is_approved ? 'default' : 'secondary'} className={event.is_approved ? 'bg-emerald-100 text-emerald-800' : ''}>
                                {statusLabels[event.status] || event.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Justificatif Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={(open) => { setIsUploadDialogOpen(open); if (!open) { setUploadFile(null); setSelectedAbsence(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un justificatif</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUploadJustificatif} className="space-y-4 py-4">
            {selectedAbsence && (
              <Card className="bg-slate-50 dark:bg-slate-800">
                <CardContent className="p-4">
                  <p className="font-medium">{selectedAbsence.employee_name}</p>
                  <p className="text-sm text-slate-500">
                    Arret maladie du {formatDateShort(selectedAbsence.date_debut)} au {formatDateShort(selectedAbsence.date_fin)}
                  </p>
                </CardContent>
              </Card>
            )}
            <div className="space-y-2">
              <Label>Justificatif (arret de travail) *</Label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files[0])}
                accept=".pdf,.jpg,.jpeg,.png"
                required
              />
              <p className="text-xs text-slate-500">Formats acceptes : PDF, JPG, PNG</p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="bg-[#0F5156] hover:bg-[#0d464a]">
                <Upload className="w-4 h-4 mr-2" />
                Uploader
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Overlap Confirmation Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={(open) => { if (!open && !submitting) handleCancelOverlap(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Attention: Superposition de conges
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Cet employe a deja des conges poses sur cette periode :
            </p>
            {overlapWarning?.overlapping && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <ul className="space-y-2">
                  {overlapWarning.overlapping.map((overlap, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-amber-500" />
                      {overlap}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {overlapWarning?.solde && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-700" data-testid="overlap-solde-warning">
                <p className="font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Solde insuffisant
                </p>
                <p className="mt-1">{overlapWarning.solde.message}</p>
                {!overlapWarning.solde.peut_forcer && (
                  <p className="mt-1 text-xs">Seul un gestionnaire (RH, admin ou manager) peut forcer une demande au-delà du solde.</p>
                )}
              </div>
            )}
            <p className="text-slate-600">
              Voulez-vous tout de même créer cette demande ? Les RH seront alertés de cette superposition.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleCancelOverlap} disabled={submitting}>
              Annuler
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleConfirmOverlap}
              disabled={submitting || Boolean(overlapWarning?.solde && !overlapWarning.solde.peut_forcer)}
              data-testid="absence-confirm-overlap-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement…
                </>
              ) : (
                'Confirmer la demande'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog overlap validation */}
      <Dialog open={!!validateOverlapWarning} onOpenChange={(open) => { if (!open) { setValidateOverlapWarning(null); setPendingValidation(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Chevauchement detecte
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Des conges deja valides chevauchent cette periode pour ce salarie :
            </p>
            {validateOverlapWarning?.overlapping && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <ul className="space-y-2">
                  {validateOverlapWarning.overlapping.map((overlap, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-amber-500" />
                      {overlap}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-slate-600">
              Voulez-vous forcer la validation malgre le chevauchement ?
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setValidateOverlapWarning(null); setPendingValidation(null); }}>
              Annuler
            </Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => {
              if (pendingValidation) {
                handleValidate(pendingValidation.id, pendingValidation.action, pendingValidation.niveau, true);
              }
            }}>
              Forcer la validation
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Absences;
